import React, { useEffect, useRef, useState } from 'react'
import Popover from '@mui/material/Popover'

/** id → setPosition({ top, left } | null) */
const menuRegistry = new Map()

function openMenu(id, position) {
  menuRegistry.get(id)?.(position)
}

/**
 * Drop-in for `react-contextmenu` ContextMenuTrigger.
 * Right-click (and optional long-press) opens the ContextMenu with the same `id`.
 */
export function ContextMenuTrigger({
  id,
  children,
  renderTag = 'div',
  holdToDisplay = -1,
  attributes = {},
  style,
  collect: _collect,
  ...rest
}) {
  const Tag = renderTag
  const holdTimer = useRef(null)

  const clearHold = () => {
    if (holdTimer.current != null) {
      clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
  }

  const showMenu = (clientX, clientY, event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    openMenu(id, { top: clientY, left: clientX  })
  }

  const {
    onClick,
    style: attrStyle,
    onContextMenu,
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    onTouchCancel,
    ...otherAttrs
  } = attributes

  return (
    <Tag
      {...rest}
      {...otherAttrs}
      style={{ ...style, ...attrStyle }}
      onClick={onClick}
      onContextMenu={(event) => {
        onContextMenu?.(event)
        showMenu(event.clientX, event.clientY, event)
      }}
      onTouchStart={(event) => {
        onTouchStart?.(event)
        if (holdToDisplay < 0) return
        const touch = event.touches?.[0]
        if (!touch) return
        holdTimer.current = setTimeout(() => {
          showMenu(touch.clientX, touch.clientY, event)
        }, holdToDisplay)
      }}
      onTouchEnd={(event) => {
        clearHold()
        onTouchEnd?.(event)
      }}
      onTouchMove={(event) => {
        clearHold()
        onTouchMove?.(event)
      }}
      onTouchCancel={(event) => {
        clearHold()
        onTouchCancel?.(event)
      }}
    >
      {children}
    </Tag>
  )
}

/**
 * Drop-in for `react-contextmenu` ContextMenu.
 * Uses MUI Popover so children can be ListItems / custom markup (not only MenuItem).
 */
export function ContextMenu({ id, children, className, sx  }) {
  const [position, setPosition] = useState(null)

  useEffect(() => {
    menuRegistry.set(id, setPosition)
    return () => {
      if (menuRegistry.get(id) === setPosition) {
        menuRegistry.delete(id)
      }
    }
  }, [id])

  const handleClose = () => setPosition(null)

  return (
    <Popover
      open={Boolean(position)}
      onClose={handleClose}
      onClick={handleClose}
      anchorReference="anchorPosition"
      anchorPosition={position || undefined}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          className,
          sx: { minWidth: 180, py: 0.5, ...sx },
        },
      }}
    >
      <div role="menu">{children}</div>
    </Popover>
  )
}

/**
 * Drop-in for `react-contextmenu` MenuItem — thin wrapper; real UI is usually a ListItem child.
 */
export function MenuItem({
  children,
  onClick,
  disabled,
  className,
  ...rest
}) {
  return (
    <div
      role="menuitem"
      className={className}
      onClick={disabled ? undefined : onClick}
      {...rest}
      style={{ outline: 'none', ...(rest.style || { }) }}
    >
      {children}
    </div>
  )
}

export default {
  ContextMenu,
  ContextMenuTrigger,
  MenuItem,
}
